import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlusCircle, FiCheckCircle, FiAlertCircle, FiLoader } from "react-icons/fi";

const CreateGroupForm = ({ theme }) => {
  const [programmeGroups, setProgrammeGroups] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [programmeGroupId, setProgrammeGroupId] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [groupStatus, setGroupStatus] = useState(true); // Default to active

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch programme groups on load
  useEffect(() => {
    const fetchProgrammeGroups = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/programme_groups`,
        );
        const data = await response.json();
        setProgrammeGroups(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch programme groups", error);
        setStatus({
          type: "error",
          message: "Failed to load programme groups"
        });
        setIsLoading(false);
      }
    };

    fetchProgrammeGroups();
  }, [backendUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });
    setIsSubmitting(true);

    try {
      const response = await fetch(`${backendUrl}/api/create-group`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Attach token if needed
        },
        body: JSON.stringify({
          group_name: groupName,
          programme_group_id: Number(programmeGroupId),
          status: groupStatus ? 1 : 0, // Convert boolean to 1/0 for backend
          created_by: 1, // Replace with actual user ID if needed
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create group");
      }

      setStatus({
        type: "success",
        message: data.message || "Group created successfully!"
      });
      setGroupName("");
      setProgrammeGroupId("");
      setGroupStatus(true); // Reset to active for next creation
    } catch (error) {
      console.error("Error creating group", error);
      setStatus({
        type: "error",
        message: error.message || "An error occurred while creating the group"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-md mx-auto mt-10 p-6 bg-white rounded-2xl shadow-lg border"
    >
      <div className="flex items-center justify-center mb-6">
        <FiPlusCircle className="text-blue-500 text-3xl mr-2" />
        <h2 className="text-2xl font-semibold text-gray-800">Create New Group</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <label className="block font-medium text-gray-700 mb-1">Group Name</label>
          <input
            type="text"
            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            required
            placeholder="Enter group name"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <label className="block font-medium text-gray-700 mb-1">Programme Group</label>
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <FiLoader className="animate-spin text-gray-500 mr-2" />
              <span>Loading programme groups...</span>
            </div>
          ) : (
            <select
              className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              value={programmeGroupId}
              onChange={(e) => setProgrammeGroupId(e.target.value)}
              required
            >
              <option value="">Select programme group</option>
              {programmeGroups.map((pg) => (
                <option key={pg.id} value={pg.id}>
                  {pg.name}
                </option>
              ))}
            </select>
          )}
        </motion.div>

        {/* Status Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="space-y-2"
        >
          <label className="block font-medium text-gray-700 mb-1">Status</label>
          <div className="flex gap-3">
            <label
              className={`flex-1 text-center px-4 py-2 rounded-lg border cursor-pointer transition ${
                groupStatus === true
                  ? "bg-green-100 border-green-400 text-green-800 shadow-inner"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="status"
                checked={groupStatus === true}
                onChange={() => setGroupStatus(true)}
                className="hidden"
              />
              Active
            </label>
            <label
              className={`flex-1 text-center px-4 py-2 rounded-lg border cursor-pointer transition ${
                groupStatus === false
                  ? "bg-red-100 border-red-400 text-red-800 shadow-inner"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="status"
                checked={groupStatus === false}
                onChange={() => setGroupStatus(false)}
                className="hidden"
              />
              Inactive
            </label>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center transition ${
              isSubmitting || isLoading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSubmitting ? (
              <>
                <FiLoader className="animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <FiPlusCircle className="mr-2" />
                Create Group
              </>
            )}
          </button>
        </motion.div>

        <AnimatePresence>
          {status.message && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className={`mt-4 p-3 rounded-lg flex items-center ${
                status.type === "error"
                  ? "bg-red-50 text-red-700"
                  : "bg-green-50 text-green-700"
              }`}
            >
              {status.type === "error" ? (
                <FiAlertCircle className="mr-2 text-lg" />
              ) : (
                <FiCheckCircle className="mr-2 text-lg" />
              )}
              <span>{status.message}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </motion.div>
  );
};

export default CreateGroupForm;